import { AnnouncementBar } from './announcement-bar';
import { Suspense, lazy } from 'react';
import type { ReactNode } from 'react';
import { createBrowserRouter } from 'react-router';
import { LinkPage } from '../auth/link-page';
import { LoginPage } from '../auth/login-page';
import { RequireSession } from '../auth/require-session';
import { HomePage } from '../child/home-page';
import { Compass, House, RotateCcw, TriangleAlert } from 'lucide-react';
import { Button, ButtonLink } from '../ui/button';
import { LoadingScreen } from '../ui/spinner';
import { StatusScreen } from '../ui/status-screen';

const AdminApp = lazy(() => import('../admin/admin-app'));
const ParentPage = lazy(async () => ({
  default: (await import('../child/parent-page')).ParentPage,
}));
const SubjectsPage = lazy(async () => ({
  default: (await import('../child/subjects-page')).SubjectsPage,
}));
const SubjectPage = lazy(async () => ({
  default: (await import('../child/subject-page')).SubjectPage,
}));
const TopicPage = lazy(async () => ({
  default: (await import('../child/subject-page')).TopicPage,
}));
const RoundRoute = lazy(async () => ({
  default: (await import('../child/round/round-page')).RoundRoute,
}));
const GamesPage = lazy(async () => ({
  default: (await import('../child/games/games-page')).GamesPage,
}));
const RekenraceIntroPage = lazy(async () => ({
  default: (await import('../child/games/rekenrace/intro-page')).RekenraceIntroPage,
}));
const RacePage = lazy(async () => ({
  default: (await import('../child/games/rekenrace/race-page')).RacePage,
}));
const WelcomePage = lazy(async () => ({
  default: (await import('../child/welcome-page')).WelcomePage,
}));
const ShopPage = lazy(async () => ({ default: (await import('../shop/shop-page')).ShopPage }));
const ThanksPage = lazy(async () => ({
  default: (await import('../shop/thanks-page')).ThanksPage,
}));
const TestPaymentPage = lazy(async () => ({
  default: (await import('../shop/test-payment-page')).TestPaymentPage,
}));
const TermsPage = lazy(async () => ({
  default: (await import('../legal/terms-page')).TermsPage,
}));
const PrivacyPage = lazy(async () => ({
  default: (await import('../legal/privacy-page')).PrivacyPage,
}));

function publicPage(page: ReactNode) {
  return <Suspense fallback={<LoadingScreen />}>{page}</Suspense>;
}

function parentOnly(page: ReactNode, audience?: 'parents' | 'children') {
  return (
    <RequireSession role="parent">
      {audience ? <AnnouncementBar audience={audience} /> : null}
      <Suspense fallback={<LoadingScreen />}>{page}</Suspense>
    </RequireSession>
  );
}

const parentPages = [
  { path: '/ouders', element: parentOnly(<ParentPage />, 'parents') },
  { path: '/welkom', element: parentOnly(<WelcomePage />, 'parents') },
  { path: '/oefenen/:childId', element: parentOnly(<SubjectsPage />, 'children') },
  { path: '/oefenen/:childId/:subjectKey', element: parentOnly(<SubjectPage />, 'children') },
  {
    path: '/oefenen/:childId/:subjectKey/:topicId',
    element: parentOnly(<TopicPage />, 'children'),
  },
  { path: '/ronde/:roundId', element: parentOnly(<RoundRoute />) },
  { path: '/spelletjes/:childId', element: parentOnly(<GamesPage />) },
  { path: '/spelletjes/:childId/rekenrace', element: parentOnly(<RekenraceIntroPage />) },
  { path: '/spel/rekenrace/:runId', element: parentOnly(<RacePage />) },
];

function NotFoundPage() {
  return (
    <StatusScreen
      icon={<Compass aria-hidden="true" />}
      eyebrow="Fout 404"
      title="Deze pagina bestaat niet"
      actions={
        <ButtonLink to="/">
          <House aria-hidden="true" />
          Naar de startpagina
        </ButtonLink>
      }
    >
      Oefy heeft overal gezocht, maar kan deze pagina niet vinden. Misschien is de link verouderd of
      is er een typfout gemaakt.
    </StatusScreen>
  );
}

/** Shown instead of React Router's developer page when any page crashes. */
function ErrorPage() {
  return (
    <StatusScreen
      icon={<TriangleAlert aria-hidden="true" />}
      tone="warning"
      title="Oeps, er ging iets mis"
      actions={
        <>
          <ButtonLink to="/" variant="secondary">
            <House aria-hidden="true" />
            Naar de startpagina
          </ButtonLink>
          <Button
            onClick={() => {
              window.location.reload();
            }}
          >
            <RotateCcw aria-hidden="true" />
            Opnieuw proberen
          </Button>
        </>
      }
    >
      Oefy liep even vast. Probeer het opnieuw. Lukt dat niet, ga dan terug naar de startpagina.
    </StatusScreen>
  );
}

const pages = [
  { path: '/inloggen', element: <LoginPage /> },
  { path: '/inloggen/link', element: <LinkPage /> },
  { path: '/abonnement', element: publicPage(<ShopPage />) },
  { path: '/abonnement/klaar', element: publicPage(<ThanksPage />) },
  { path: '/testbetaling', element: publicPage(<TestPaymentPage />) },
  { path: '/algemene-voorwaarden', element: publicPage(<TermsPage />) },
  { path: '/privacyverklaring', element: publicPage(<PrivacyPage />) },
  {
    path: '/beheer/*',
    element: (
      <Suspense fallback={<LoadingScreen />}>
        <AdminApp />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <RequireSession>
        <AnnouncementBar audience="children" />
        <HomePage />
      </RequireSession>
    ),
  },
  ...parentPages,
  { path: '*', element: <NotFoundPage /> },
];

// One root route without a path catches errors from every page.
export const router = createBrowserRouter([{ errorElement: <ErrorPage />, children: pages }]);
